import type * as gamesApi from "@pax/api-contract/games/v1alpha1";
import type { GameId, PlayerConnection } from "@pax/game-server-contract/games/v1alpha1";
import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { api, gameServer, orpc, reachable } from "@/lib/api";

export const gamesKey = orpc.games.list.key();
const pendingGameConnections = new Map<string, Promise<PlayerConnection>>();

export const gamesQuery = queryOptions({
  queryKey: gamesKey,
  queryFn: async () => {
    const games: gamesApi.GameSummary[] = [];
    let cursor: string | undefined;
    do {
      const page = await reachable(
        api.games.list({
          query: { limit: 100, ...(cursor ? { cursor } : {}) },
        }),
      );
      games.push(...page.items);
      cursor = page.nextCursor ?? undefined;
    } while (cursor);
    return games;
  },
});

export const gameQuery = (id: string) =>
  orpc.games.get.queryOptions({
    input: { params: { id } },
    select: response => response.game,
  });

/**
 * Makes the current user a member of the game a link named, when the link lets them in, and
 * otherwise asks to join it. Calling again once they're let in joins.
 */
export async function joinGame(
  queryClient: QueryClient,
  id: string,
): Promise<gamesApi.JoinGameResponse> {
  const joined = await reachable(api.games.join({ params: { id } }));
  if (joined.status === "joined") {
    queryClient.setQueryData(gameQuery(id).queryKey, { game: joined.game });
    void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
  }
  return joined;
}

/** Renames the game or changes who its link lets in, then shows the change everywhere it's listed. */
export async function updateGame(
  queryClient: QueryClient,
  id: string,
  body: gamesApi.UpdateGameRequest,
): Promise<gamesApi.GameSummary> {
  const { game } = await reachable(api.games.update({ params: { id }, body }));
  queryClient.setQueryData(gameQuery(id).queryKey, { game });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
  return game;
}

/**
 * Opens the game to others, once it passes review, or makes it singleplayer again, which removes
 * everyone else. Shows the change everywhere the game is listed.
 */
export async function setGameMode(
  queryClient: QueryClient,
  id: string,
  mode: gamesApi.SetGameModeRequest["mode"],
): Promise<gamesApi.GameSummary> {
  const { game } = await reachable(api.games.mode.set({ params: { id }, body: { mode } }));
  queryClient.setQueryData(gameQuery(id).queryKey, { game });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
  void queryClient.invalidateQueries({ queryKey: gameMembersQuery(id).queryKey });
  return game;
}

export const gameMembersQuery = (id: string) =>
  orpc.games.members.list.queryOptions({
    input: { params: { id } },
    select: response => response.members,
  });

/** Takes someone out of the game, or the current user, which is leaving it. */
export async function removeMember(
  queryClient: QueryClient,
  id: string,
  userId: gamesApi.GameMember["userId"],
  leaving: boolean,
): Promise<void> {
  await reachable(api.games.members.delete({ params: { id, userId } }));
  if (leaving) queryClient.removeQueries({ queryKey: gameQuery(id).queryKey, exact: true });
  else void queryClient.invalidateQueries({ queryKey: gameMembersQuery(id).queryKey });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
}

export const joinRequestsQuery = (id: string) =>
  orpc.games.joinRequests.list.queryOptions({
    input: { params: { id } },
    select: response => response.requests,
  });

export async function answerJoinRequest(
  queryClient: QueryClient,
  id: string,
  userId: gamesApi.JoinRequest["userId"],
  answer: gamesApi.AnswerJoinRequestRequest["answer"],
): Promise<void> {
  await reachable(api.games.joinRequests.answer({ params: { id, userId }, body: { answer } }));
  void queryClient.invalidateQueries({ queryKey: joinRequestsQuery(id).queryKey });
  void queryClient.invalidateQueries({ queryKey: gameQuery(id).queryKey, exact: true });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
}

export type CreateGameInput = Omit<gamesApi.CreateGameRequest, "id"> & { id: string };

export async function createGame(
  queryClient: QueryClient,
  input: CreateGameInput,
): Promise<gamesApi.GameSummary> {
  const response = await reachable(api.games.create({ body: input }));
  const { game } = response.body;
  queryClient.setQueryData(gameQuery(game.id).queryKey, { game });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
  return game;
}

export async function forkGame(
  queryClient: QueryClient,
  id: string,
  body: gamesApi.ForkGameRequest,
): Promise<gamesApi.GameSummary> {
  const { game } = await reachable(api.games.fork({ params: { id }, body }));
  queryClient.setQueryData(gameQuery(game.id).queryKey, { game });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
  return game;
}

export function connectGame(id: string) {
  const pending = pendingGameConnections.get(id);
  if (pending) return pending;
  const connection = reachable(gameServer.games.connect({ params: { id: id as GameId } }));
  pendingGameConnections.set(id, connection);
  void connection
    .finally(() => {
      if (pendingGameConnections.get(id) === connection) pendingGameConnections.delete(id);
    })
    .catch(() => undefined);
  return connection;
}

/** Creates, updates, or deletes one of the game's skills. The change arrives on the game socket. */
export function editSkill(
  id: string,
  edit: gamesApi.SkillEdit,
): Promise<gamesApi.LobbyCommandResponse> {
  return reachable(
    edit.action === "create"
      ? api.games.skills.create({ params: { id }, body: edit.skill })
      : edit.action === "update"
        ? api.games.skills.update({
            params: { id, skillId: edit.skillId },
            body: { changes: edit.changes, basedOnRevision: edit.basedOnRevision },
          })
        : api.games.skills.delete({ params: { id, skillId: edit.skillId } }),
  );
}

export function selectGameMaster(
  id: string,
  agentVersionId: gamesApi.SelectGameMasterRequest["agentVersionId"],
) {
  return reachable(api.games.gameMaster.set({ params: { id }, body: { agentVersionId } }));
}

export async function deleteGame(queryClient: QueryClient, id: string): Promise<void> {
  await reachable(api.games.delete({ params: { id } }));
  queryClient.removeQueries({ queryKey: gameQuery(id).queryKey, exact: true });
  void queryClient.invalidateQueries({ queryKey: gamesKey, exact: true });
}
